import React, { useState, useEffect } from 'react';
import { Trash2, Mail, MessageSquare, Eye, Check, Clock, Search } from 'lucide-react';
import { getContactMessages, updateMessageStatus, deleteContactMessage } from '../../utils/api';
import { toast } from 'sonner';
import './AdminStyles.css';

const ContactManagement = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const data = await getContactMessages();
      setMessages(data);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (messageId, newStatus) => {
    try {
      await updateMessageStatus(messageId, newStatus);
      setMessages(messages.map(m => 
        m.id === messageId ? { ...m, status: newStatus } : m
      ));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage({ ...selectedMessage, status: newStatus });
      }
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await deleteContactMessage(messageId);
      setMessages(messages.filter(m => m.id !== messageId));
      if (selectedMessage?.id === messageId) {
        setSelectedMessage(null);
      }
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    if (message.status === 'new') {
      await handleStatusChange(message.id, 'read');
    }
  };

  const filteredMessages = messages.filter(m => {
    const matchesSearch = 
      m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || m.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new': return <Clock size={14} />;
      case 'read': return <Eye size={14} />;
      case 'replied': return <Check size={14} />;
      default: return null;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const newCount = messages.filter(m => m.status === 'new').length;

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="admin-page contact-management" data-testid="contact-management">
      <div className="admin-header">
        <div>
          <h1>Contact Messages</h1>
          <p className="admin-subtitle">
            {messages.length} total messages
            {newCount > 0 && <span className="new-badge">{newCount} new</span>}
          </p>
        </div>
      </div>

      <div className="filters-row">
        <div className="search-bar">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-messages"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="status-filter"
          data-testid="status-filter"
        >
          <option value="all">All Status</option>
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="replied">Replied</option>
        </select>
      </div>

      <div className="messages-layout">
        <div className="messages-list">
          {filteredMessages.length === 0 ? (
            <div className="empty-state">
              <MessageSquare size={48} />
              <h3>{searchTerm || statusFilter !== 'all' ? 'No matching messages' : 'No messages yet'}</h3>
              <p>Contact form submissions will appear here</p>
            </div>
          ) : (
            filteredMessages.map((message) => (
              <div
                key={message.id}
                className={`message-item ${message.status} ${selectedMessage?.id === message.id ? 'selected' : ''}`}
                onClick={() => openMessage(message)}
                data-testid={`message-item-${message.id}`}
              >
                <div className="message-header">
                  <span className="message-sender">{message.name}</span>
                  <span className={`status-badge ${message.status}`}>
                    {getStatusIcon(message.status)}
                    {message.status}
                  </span>
                </div>
                <div className="message-subject">{message.subject}</div>
                <div className="message-preview">{message.message.substring(0, 80)}...</div>
                <div className="message-meta">
                  <span>{message.email}</span>
                  <span>{formatDate(message.created_at)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {selectedMessage && (
          <div className="message-detail" data-testid="message-detail">
            <div className="detail-header">
              <h2>{selectedMessage.subject}</h2>
              <div className="detail-actions">
                <select
                  value={selectedMessage.status}
                  onChange={(e) => handleStatusChange(selectedMessage.id, e.target.value)}
                  className="status-select"
                  data-testid="change-status"
                >
                  <option value="new">New</option>
                  <option value="read">Read</option>
                  <option value="replied">Replied</option>
                </select>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDelete(selectedMessage.id)}
                  data-testid="delete-message"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            
            <div className="detail-meta">
              <div className="sender-info">
                <strong>From:</strong> {selectedMessage.name} ({selectedMessage.email})
              </div>
              <div className="date-info">
                <strong>Received:</strong> {formatDate(selectedMessage.created_at)}
              </div>
            </div>

            <div className="detail-body">
              <p>{selectedMessage.message}</p>
            </div>

            <div className="detail-footer">
              <a 
                href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                className="btn btn-primary"
                onClick={() => handleStatusChange(selectedMessage.id, 'replied')}
                data-testid="reply-email"
              >
                <Mail size={18} />
                Reply via Email
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactManagement;
